import { BBPlayerAPI } from './../../../interfaces/bb-player-api';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { AnalyticsRangeDataset, AnalyticsService, StatsDateRange } from './../../../services/analytics.service';
import { Component, Input, Output, EventEmitter, SimpleChanges, ViewChild, OnInit, OnChanges } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import {
	ProjectStatsConfiguration,
	ProjectWidget
} from '../../../interfaces/project';
import { Mediaclip } from '../../../interfaces/mediaclip';
import { Publication } from '../../../interfaces/publication';
import { SapiService } from '../../../services/sapi.service';

import { DateRange } from '../../../interfaces/dateRange';
import { IDataTrackAreaItem } from '../interactivity-editor/redux/timeline-data.model';
import { LocationService } from '../../../services/location.service';

import dayjs from 'dayjs';
import { ChartArea, ChartDataset } from 'chart.js';
import { BbCanvasElementComponent } from '../bb-canvas-element/bb-canvas-element.component';

const HOUR_IN_MILLISECONDS = 3600000;
@Component({
	selector: 'stats-progress',
	templateUrl: './stats-progress.component.html',
	styleUrls: ['./stats-progress.component.scss']
})

export class StatsProgressComponent implements OnInit, OnChanges {
	@ViewChild(BbCanvasElementComponent) canvasElementComponent: BbCanvasElementComponent;

	@Input() clipId: string;
	@Input() configuration: ProjectStatsConfiguration = {};
	@Input() parentEntityType: string;
	@Input() parentEntityId: string;
	@Input() hideViews = false;
	@Input() mode: 'analytics-dashboard' | 'in-modal' | 'default' = 'default';
	@Input() showFilterWarning = false;

	@Output() mediaclipChanged: EventEmitter<any> = new EventEmitter<any>();
	@Output() navigationOccurred: EventEmitter<string> = new EventEmitter<string>();

	mediaclip: Mediaclip;
	highlightWidgets = true;
	embed = false;
	embedCode: string;
	viewpageUrl: string;
	publication: Publication;
	chartOptions: any;
	playerApi: BBPlayerAPI = null;
	chart: any;
	currentTime: number;
	mouseOverPct: number;
	widgets: ProjectWidget[] = [];
	playerOverlayStyling: any = {};
	numVisibleWidgets = 0;
	views: number;
	playerApiLoaded = false;
	dropoffChartPosition = 'initial';

	statsMinDate: Date;

	viewCountGraphDatasets: ChartDataset[];
	viewRangeGraphDatasets: ChartDataset[];
	viewRangeNoData = false;
	viewCountNoData = false;
	viewCountLoading = false;

	templateId: string;
	timelineId: string;
	chartArea: ChartArea;
	timeSliderCanvasContext: CanvasRenderingContext2D;

	constructor(
		private sapiService: SapiService,
		private locationService: LocationService,
		private translate: TranslateService,
		public analyticsService: AnalyticsService
	) { }

	ngOnInit() {
		if (this.clipId) {
			this.getMediaclip(this.clipId);
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes?.clipId?.currentValue && !changes.clipId.firstChange && (this.clipId !== changes.clipId.currentValue)) {
			this.getMediaclip(changes.clipId.currentValue);
		}

		if (changes?.configuration?.currentValue && !changes.configuration.firstChange && (
			changes.configuration.previousValue.statsDateStart !== changes.configuration.currentValue.statsDateStart ||
			changes.configuration.previousValue.statsDateEnd !== changes.configuration.currentValue.statsDateEnd
		)) {
			this.getMediaclipInteractiveStats(this.mediaclip?.id);
		}
	}

	onTemplateIdChange(ev) {
		if (!this.templateId) {
			this.getMediaclipInteractiveStats(this.mediaclip.id);
			return;
		}
		this.timelineId = null;
		this.getTemplate(this.templateId);
	}

	onTimelineIdChange(ev) {
		if (!this.timelineId) {
			this.getMediaclipInteractiveStats(this.mediaclip.id);
			return;
		}
		this.templateId = null;
		this.getTimeline(this.timelineId);
	}

	updateCanvasContext(context: CanvasRenderingContext2D) {
		this.timeSliderCanvasContext = context;
		this.updateTimeSlider(this.currentTime);
	}

	updateChartArea(chartArea: ChartArea) {
		this.chartArea = chartArea;
		this.updateTimeSlider(0);
	}

	updateTimeSlider(currentTime: number) {
		const milliSeconds: number = currentTime * 1000;
		if (this.timeSliderCanvasContext && this.chartArea) {
			const chartWidth = this.chartArea.right - this.chartArea.left;
			const position = this.chartArea.left + ((chartWidth / this.mediaclip.length) * currentTime);
			if (this.canvasElementComponent) {
				this.canvasElementComponent.clearCanvas();
			}
			this.timeSliderCanvasContext.beginPath();
			this.timeSliderCanvasContext.globalAlpha = 0.4;
			this.timeSliderCanvasContext.strokeStyle = 'red';
			this.timeSliderCanvasContext.moveTo(position, 0);
			this.timeSliderCanvasContext.lineTo(position, 250);
			this.timeSliderCanvasContext.lineWidth = 1;
			this.timeSliderCanvasContext.stroke();
			this.timeSliderCanvasContext.font = '13px Lato';
			this.timeSliderCanvasContext.fillText(this.getTimeFormat(milliSeconds), position + 5, 18);
			this.timeSliderCanvasContext.fillStyle = '#858585';
			this.timeSliderCanvasContext.closePath();
		}
	}

	getTimeFormat(milliSeconds: number): string {
		if (milliSeconds > 0) {
			let format = 'mm:ss';
			if (milliSeconds > HOUR_IN_MILLISECONDS) {
				format = 'HH:mm:ss';
			}
			return dayjs(milliSeconds).utc().format(format);
		}
		return '';
	}

	onMediaclipLoaded(mediaclip: Mediaclip) {
		// this.timeSliderCanvas.nativeElement.width = this.timeSliderCanvas.nativeElement.parent.width;
		if (!mediaclip) {
			return;
		}
		if (!this.mediaclip || this.mediaclip.id !== mediaclip.id) {
			this.mediaclip = mediaclip;
			this.clipId = mediaclip.id;
			this.updateTimeline(this.mediaclip);
			this.setMinDate(this.mediaclip);
			this.getProgressStats();
			this.getMediaclipInteractiveStats(this.mediaclip.id);
			this.mediaclipChanged.emit(this.mediaclip);
			this.getClipViewsRangeAndPlayerLoads(this.mediaclip.id);
		}
	}

	// get stats for line graph (top right)
	getClipViewsRangeAndPlayerLoads(clipId: string) {
		if (!clipId || !this.configuration || !this.configuration.statsDateStart) {
			return;
		}

		this.views = null;

		const dateRange: StatsDateRange = {
			fromDate: this.configuration.statsDateStart.toISOString(),
			toDate: this.configuration.statsDateEnd.toISOString()
		};
		this.analyticsService.setDateRange(dateRange, false);
		this.analyticsService.setGranularity('day', false);
		this.analyticsService.getClipRangeStats(clipId, true).subscribe((data: AnalyticsRangeDataset) => {
			if (!data || data.total === 0) {
				this.viewRangeNoData = true;
				return;
			}
			this.viewRangeNoData = false;
			this.viewRangeGraphDatasets = [];
			this.viewRangeGraphDatasets.push(data.chartDataset);
			this.views = data.total || 0;
		});
		this.analyticsService.getPlayerLoads(clipId);

		// fetch engagement stats
		this.analyticsService.setRoute('mediaclip', this.clipId);
		this.analyticsService.getClipRangeStats(clipId).subscribe();
	}

	updateTimeline(mediaclip: Mediaclip) {
		if (mediaclip?.timelines?.[0]?.id) {
			this.getTimeline(mediaclip.timelines[0].id);
		}
	}

	getTimeline(timelineId) {
		this.sapiService.get('timeline', timelineId)
			.pipe(catchError((err) => {
				this.setTableData([], []);
				return of(null);
			}))
			.subscribe((timeline: any) => {
				if (timeline) {
					this.getMediaclipInteractiveStats(this.mediaclip.id);
				}
				if (timeline?.order?.length) {
					this.setTableData(this.widgets, timeline.order);
				} else if (timeline?.timeevents?.length) {
					this.widgets = this.processWidgetsTimeEvents(this.widgets, timeline.timeevents);
					this.setTableData(this.widgets);
				}
			});
	}

	getTemplate(templateId) {
		this.sapiService.get('app', templateId).subscribe((template: any) => {
			if (template?.timelines?.[0]?.id) {
				this.getTimeline(template?.timelines?.[0]?.id);
			}
		});
	}

	datesChanged(dateRange: DateRange) {
		if (!this.configuration) {
			this.configuration = {};
		}
		this.configuration.statsDateStart = dateRange.startDate;
		this.configuration.statsDateEnd = dateRange.endDate;
		this.getMediaclipInteractiveStats(this.clipId);
		this.getProgressStats();
		this.getClipViewsRangeAndPlayerLoads(this.clipId);
	}

	getWidgetPropValue(widget: any, prop: string) {
		if (!widget[prop]) {
			if (widget && widget.statesets && widget.statesets.length) {
				const defaultStateset = widget.statesets.find((stateset: any) => stateset.name === 'default');
				if (defaultStateset) {
					const defaultState = defaultStateset.states.find((state: any) => state.name === 'default');
					if (defaultState) {
						widget[prop] = defaultState.config[prop];
					}
				}
			}
		}

		return widget[prop];
	}

	setTableData(widgets: any[], order?: any) {
		if (typeof widgets === 'undefined' || !widgets) {
			return;
		}
		this.widgets = [];
		[...widgets].forEach((widget: any) => {
			if (!widget.id) {
				return;
			}
			widget.inApp = false;
			this.widgets.push(widget);
			if (widget.widgets && widget.widgets.length) {
				widget.widgets.forEach((subWidget: any) => {
					if (!subWidget.id) {
						return;
					}
					subWidget.inApp = true;
					subWidget.parentWidget = widget;
					this.widgets.push(subWidget);
				});
			}
		});
		if (order && order.length) {
			this.widgets = [...this.processWidgetsOrder(this.widgets, order)];
		}
	}

	processWidgetsOrder(widgets: any[], order: any) {
		const flatWidgets: any[] = [];
		const appWidgets: any = {};
		if (widgets && widgets.length) {
			widgets.forEach((widget) => {
				if (widget) {
					flatWidgets.push(widget);
					if (widget.widgets && widget.widgets.length) {
						widget.widgets.forEach((appWidget: any) => {
							appWidget.inApp = true;
							appWidget.parentWidget = widget;
							flatWidgets.push(appWidget);
						});
						appWidgets[widget.id] = widget;
					}
				}
			});
		}
		let orderedWidgets: any[] = [];
		if (order && order.length) {
			order.forEach((track: IDataTrackAreaItem) => {
				if (track?.tracks?.length) {
					track?.tracks.forEach((groupedTrack) => {
						const gw = flatWidgets.find((w2: any) => groupedTrack.widgetid === w2.id);
						if (gw) {
							if (groupedTrack && groupedTrack.inoutpoints && groupedTrack.inoutpoints[0]) {
								gw.inpoint = String(groupedTrack.inoutpoints[0].inpoint);
								gw.outpoint = String(groupedTrack.inoutpoints[0].outpoint);
							} else {
								gw.inpoint = '0%';
								gw.outpoint = '100%';
							}
							orderedWidgets.push(gw);

							if (appWidgets[gw.id] && appWidgets[gw.id].widgets && appWidgets[gw.id].widgets.length) {
								orderedWidgets = orderedWidgets.concat(appWidgets[gw.id].widgets);
							}
						}
					});
				} else {
					const w = flatWidgets.find((w2: any) => track.widgetid === w2.id);
					if (w) {
						if (track && track.inoutpoints && track.inoutpoints[0]) {
							w.inpoint = String(track.inoutpoints[0].inpoint);
							w.outpoint = String(track.inoutpoints[0].outpoint);
						} else {
							w.inpoint = '0%';
							w.outpoint = '100%';
						}
						orderedWidgets.push(w);

						if (appWidgets[w.id] && appWidgets[w.id].widgets && appWidgets[w.id].widgets.length) {
							orderedWidgets = orderedWidgets.concat(appWidgets[w.id].widgets);
						}
					}
				}
			});
		}
		return orderedWidgets;
	}

	processWidgetsTimeEvents(widgets: any, timeevents: any) {
		if (timeevents && timeevents.length) {
			timeevents.forEach((timeevent: any) => {
				const wid = timeevent && timeevent.widget && timeevent.widget.id;
				const w = widgets.find((w2: any) => wid === w2.id) ||
					(widgets && widgets.widgets &&
						widgets.widgets.find((w2: any) => wid === w2.id));
				if (w) {
					w.inpoint = String(timeevent.inpoint);
					w.outpoint = String(timeevent.outpoint);
				}
			});
		}
		return widgets;
	}

	// get stats for widgets table
	getMediaclipInteractiveStats(id: string) {
		if (!id || !this.configuration || !this.configuration.statsDateStart) {
			return;
		}
		const params: any = {
			fromDate: this.configuration.statsDateStart.toISOString(),
			toDate: this.configuration.statsDateEnd.toISOString(),
		};

		if (this.parentEntityType) {
			params.parentEntityType = this.parentEntityType;
		}

		if (this.parentEntityId) {
			params.parentEntityId = this.parentEntityId;
		}

		if (this.timelineId) {
			params.timelineId = this.timelineId;
		} else if (this.templateId) {
			params.appId = this.templateId;
		}

		this.sapiService.getUrlFromBase(`/sapi/commonclip/${id}/interactivestats`, params)
			.pipe(catchError((err) => {
				console.warn('getMediaclipInteractiveStats err', err);
				this.setTableData([], []);
				return of(null);
			}))
			.subscribe((statsData) => {
				const widgets = statsData?.widgets?.widgets ? statsData.widgets.widgets : statsData?.widgets || [];

				if (widgets?.length) {
					this.setTableData(widgets, statsData.order);
				}
			});
	}

	setMinDate(mediaclip: Mediaclip) {
		const pubDate = new Date(mediaclip.publisheddate);
		const haveStatsSinceDate = new Date('2020-01-29'); // single mediaclip statistics available since approx 2020-01-29
		this.statsMinDate = !this.parentEntityId && pubDate.getTime() < haveStatsSinceDate.getTime() ? haveStatsSinceDate : pubDate;
	}

	getMediaclip(id: string) {
		if (!id) {
			return;
		}

		this.sapiService.get('commonclip', id).subscribe((mediaclip: Mediaclip) => {
			this.onMediaclipLoaded(mediaclip);
		});
	}

	getSheetDownloadUrl() {
		if (!this.configuration) {
			return '';
		}
		return `${this.locationService.server}/sapi/commonclip/${this.clipId
		}/sheet?fromDate=${this.configuration.statsDateStart.toISOString()
		}&toDate=${this.configuration.statsDateEnd.toISOString()}${
			(this.timelineId && '&timelineId=' + this.timelineId) || (this.templateId && '&templateId=' + this.templateId) || ''
		}`;
	}

	// get stats for engagement graph (lower right)
	getProgressStats() {
		if (!this.clipId || !this.configuration) {
			return;
		}
		const params = {
			q: 'fromDate:[' + this.configuration.statsDateStart.toISOString() + '%20TO%20' + this.configuration.statsDateEnd.toISOString() + ']' +
				'%20AND%20' + 'eid:' + this.clipId,
			fromDate: this.configuration.statsDateStart.toISOString(),
			toDate: this.configuration.statsDateEnd.toISOString(),
			facet: 'true',
			rows: '0',
			fq: 'type:Mediaclip',
			'facet.field': 'percentageViewed_int',
			'facet.mincount': '1',
			'f.percentageViewed_int.facet.limit': '202',
			stats: 'true',
			'stats.field': 'percentageViewed_int'
		};

		this.viewCountLoading = true;
		this.sapiService.getUrlFromBase('/sapi/stats/', params).subscribe((r: any) => {
			this.viewCountLoading = false;
			if (r && r.facet_counts && r.facet_counts.facet_fields && r.facet_counts.facet_fields.percentageViewed_int && r.response.numFound) {
				this.viewCountGraphDatasets = [];
				this.viewCountNoData = false;

				const viewCountPercentages = this.processViewCountIntResult(r.facet_counts.facet_fields.percentageViewed_int, r.response.numFound);
				const data = viewCountPercentages.map((viewers: number, index: number) => {
					let tooltipTitle = '';
					if (this.mediaclip.length) {
						const milliSeconds = Math.round(this.mediaclip.length * index * 10);
						if (index > 0) {
							tooltipTitle = this.getTimeFormat(milliSeconds) + ' (' + index + '%)';
						}
					}
					return { x: index, y: viewers, tooltipTitle };
				});

				const dataset: ChartDataset = {
					label: this.translate.instant('VIEW_SESSIONS'),
					parsing: false,
					data,
					backgroundColor: ['#7AB1DF'],
					borderColor: ['#7AB1DF'],
					borderWidth: 1
				};

				this.viewCountGraphDatasets.push(dataset);
			} else {
				this.viewCountNoData = true;
			}
		});
	}

	processViewCountIntResult(facetArray: any[], totalViews: number) {
		const pctArray = [];
		let viewersLeft = totalViews;
		const processedFacet = this.processFacet(facetArray);
		for (let i = 0; i <= 100; i++) {
			const dropOff = processedFacet['n' + i];
			viewersLeft = dropOff ? (viewersLeft - dropOff) : viewersLeft;
			pctArray[i] = viewersLeft;
		}
		return pctArray;
	}

	processFacet(facetArray: any[]) {
		const newArray = [];
		for (let i = 0; i < facetArray.length; i = i + 2) {
			if (typeof Number(facetArray[i]) === 'number') {
				newArray['n' + facetArray[i]] = facetArray[i + 1];
			} else {
				newArray[facetArray[i]] = facetArray[i + 1];
			}
		}
		return newArray;
	}

	isCurrentlyVisible(widget: ProjectWidget, currentTime: number) {
		return (currentTime >= (Number(widget.inpoint) - 0.1) && currentTime <= (Number(widget.outpoint) + 0.1));
	}

	toString(val: any): string {
		return String(val);
	}

	checkVisibleWidgets(widgets: any, parentWidget?: any) {
		this.numVisibleWidgets = 0;
		widgets.forEach((widget: ProjectWidget) => {
			if (widget) {
				if (widget.inpoint && widget.outpoint) {
					if ((widget.inpoint.indexOf('%') !== -1 || widget.outpoint.indexOf('%') !== -1) && parentWidget) {
						widget.currentlyVisible = this.isCurrentlyVisible(parentWidget, this.currentTime);
					} else {
						widget.currentlyVisible = this.isCurrentlyVisible(widget, this.currentTime);
					}
				}

				if (widget.currentlyVisible) {
					this.numVisibleWidgets++;
				}
				if (widget.widgets && widget.widgets.length) {
					this.checkVisibleWidgets(widget.widgets, widget);
				}
			}
		});
	}

	playPause() {
		if (this.playerApi) {
			if (this.playerApi.isPlaying()) {
				this.playerApi.pause();
			} else {
				this.playerApi.play();
			}
		}
	}

	playerCreated(player: any) {
		this.playerApi = player;
		player.on('loadedclipdata', (ev: any) => {
			const clip = player.getClipData();
			if (!this.mediaclip || this.mediaclip.id !== clip.id) {
				this.getMediaclip(clip.id);
			}
			this.setOverlaySize();
			this.updateTimeSlider(0);
		});

		player.on('timeupdate', (ev: any) => {
			this.currentTime = player.getCurrentTime();
			this.updateTimeSlider(this.currentTime);
			if (this.widgets) {
				this.checkVisibleWidgets(this.widgets);
			}
		});

		player.on('resized', (ev: any) => {
			this.setOverlaySize();
		});

		player.on('started', (ev: any) => {
			this.setOverlaySize();
		});

		setTimeout(() => {
			this.playerApiLoaded = true;
		});
	}

	setOverlaySize() {
		const wrapper = this.playerApi.getWrapper();
		const bbFitme = wrapper.querySelector('.bb-fitme');
		this.playerOverlayStyling.left = bbFitme.style.left || 0;
		this.playerOverlayStyling.top = bbFitme.style.top || 0;
		this.playerOverlayStyling.width = bbFitme.style.width || '100%';
		this.playerOverlayStyling.height = bbFitme.style.height || '100%';
		this.dropoffChartPosition = 'absolute';
		setTimeout(() => {
			this.dropoffChartPosition = 'initial';
		}, 5);
	}

	seekToWidgetPoint(widget: ProjectWidget, type: string, parentWidget?: ProjectWidget) {
		let point = type === 'out' ? widget.outpoint : widget.inpoint;
		if (point.indexOf('%') !== -1 && parentWidget) {
			point = type === 'out' ? parentWidget.outpoint : parentWidget.inpoint;
		}

		this.playerApi.seek(Number(point) < 0.1 ? 0.1 : point);
	}

	progressIndicatorClick(event: MouseEvent) {
		if (event.offsetX >= this.chartArea.left && event.offsetX <= this.chartArea.right) {
			const canvasWidth = this.chartArea.right - this.chartArea.left;
			const seconds = (event.offsetX - this.chartArea.left) / canvasWidth * this.mediaclip.length;
			if (this.playerApi) {
				this.playerApi.seek(seconds);
			}
			this.updateTimeSlider(seconds);
		}
	}

	navigatingAway(id: string) {
		this.navigationOccurred.emit(id);
	}

	ticksCallbackX(value) {
		if (value % 20 === 0) {
			return value;
		} else {
			return '';
		}
	}

	ngDestroy() {
	}

}
